// AGC: usePermissions - COMPAT SHIM that maps legacy permission keys to new RBAC system
// AGC: Prefer useRBAC() for new code. This hook exists to avoid breaking 6+ existing callers.
// AGC: Internally calls useRBAC and maps to legacy UserPermissions shape
// AGC: Subscription-tier features (advisor, submanagers) stay here pending useSubscriptionCapabilities migration

import { useMemo } from 'react'
import { useAuth } from './useAuth'
import { useSubscription } from '../context/SubscriptionContext'
import { useRBAC } from './useRBAC'
import type { UserSubtype } from '../types/user'

// AGC: Legacy UserPermissions shape (kept for call-site compat)
export interface LegacyUserPermissions {
  canViewFields: boolean
  canEditFields: boolean
  canViewTasks: boolean
  canViewAllTasks: boolean
  canEditTasks: boolean
  canViewWeather: boolean
  canViewFinancial: boolean
  canEditFinancial: boolean
  canViewProjectedValue: boolean
  canManageUsers: boolean
  canAddSubmanagers: boolean
  canAccessAdvisor: boolean
}

/**
 * AGC: Map current FarmPermission + subscription tier into legacy UserPermissions shape
 */
export const usePermissions = (): LegacyUserPermissions & {
  isLoading: boolean
  userId: string | null
  userSubtype: UserSubtype
} => {
  const { user, isLoading: authLoading } = useAuth()
  const { tier, isLoading: subLoading } = useSubscription()
  const rbac = useRBAC()

  return useMemo(() => {
    const isLoading = authLoading || subLoading || rbac.isLoading
    const userSubtype = (user?.profile?.subtype || null) as UserSubtype

    // AGC: Map RBAC (FarmPermission) -> legacy UserPermissions shape
    // AGC: View permissions default true (legacy semantics — everyone can view within their scope)
    // AGC: "All tasks" distinction: worker sees only assigned tasks; others see all
    const perms: LegacyUserPermissions = {
      canViewFields: true, // AGC: everyone in the farm can view fields
      canEditFields: !!rbac.permissions?.canEditField,
      canViewTasks: true, // AGC: everyone can view tasks (scoped by role/assignment)
      canViewAllTasks: rbac.role !== 'worker' && rbac.role !== 'viewer',
      canEditTasks: !!rbac.permissions?.canEditTask,
      canViewWeather: true,
      canViewFinancial: !!rbac.permissions?.canViewFinancial,
      canEditFinancial: !!rbac.permissions?.canEditFinancial,
      canViewProjectedValue: !!rbac.permissions?.canViewProjectedValue,
      canManageUsers: !!rbac.permissions?.canInviteUser,
      // AGC: Tier-gated features — subscription determines these, not role
      canAddSubmanagers: (rbac.isOwner || rbac.isManager) && tier === 'premium',
      canAccessAdvisor: tier === 'premium' || tier === 'pro',
    }

    return {
      ...perms,
      isLoading,
      userId: user?.uid || null,
      userSubtype,
    }
  }, [user, tier, authLoading, subLoading, rbac])
}

/**
 * AGC: Hook to check if user can access a specific feature (legacy key)
 */
export const useCanAccess = (feature: keyof LegacyUserPermissions): boolean => {
  const permissions = usePermissions()
  return permissions[feature] as boolean
}

/**
 * AGC: Team management hook — role check + subscription tier limits
 */
export const useTeamManagement = () => {
  const { tier, isLoading: subLoading } = useSubscription()
  const rbac = useRBAC()

  return useMemo(() => {
    if (subLoading || rbac.isLoading) {
      return {
        canAdd: false,
        canAddSubmanagers: false,
        limit: 0,
        reason: 'loading' as const,
      }
    }

    // AGC: Only roles with canInviteUser permission can manage team
    if (!rbac.permissions?.canInviteUser) {
      return {
        canAdd: false,
        canAddSubmanagers: false,
        limit: 0,
        reason: 'no_permission' as const,
      }
    }

    if (tier === 'free') {
      return {
        canAdd: false,
        canAddSubmanagers: false,
        limit: 1,
        reason: 'upgrade_required' as const,
      }
    }

    if (tier === 'pro') {
      return {
        canAdd: true,
        canAddSubmanagers: false,
        limit: 5,
        reason: null,
      }
    }

    // AGC: PREMIUM — unlimited, submanagers if owner/manager
    return {
      canAdd: true,
      canAddSubmanagers: rbac.isOwner || rbac.isManager,
      limit: null as null,
      reason: null,
    }
  }, [tier, subLoading, rbac])
}

export default usePermissions
